import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col } from "reactstrap";
import { Map, InfoWindow, Marker, GoogleApiWrapper } from "google-maps-react";
import PropTypes from 'prop-types';
import { connect } from "react-redux";
import api from "API/Api";
import PageLoader from "./../../assets/loading/pageLoad1.gif";

// Entity type config with colors and icons
const ENTITY_TYPES = {
  companies: { label: 'Companies', color: '#e74c3c', icon: '🏢' },
  plants: { label: 'Plants', color: '#2ecc71', icon: '🏭' },
  chilling_centers: { label: 'Chilling Centers', color: '#3498db', icon: '❄️' },
  collection_centers: { label: 'Collection Centers (VLCC)', color: '#f39c12', icon: '🥛' },
  producers: { label: 'Producers', color: '#9b59b6', icon: '👨‍🌾' },
};

const LoadingContainer = () => (
  <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh'}}>
    <img src={PageLoader} alt="Loading..." />
  </div>
);

const MapView = (props) => {
  const [locations, setLocations] = useState({});
  const [summary, setSummary] = useState({});
  const [pageLoad, setPageLoad] = useState(true);
  const [activeMarker, setActiveMarker] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [filters, setFilters] = useState({
    companies: true,
    plants: true,
    chilling_centers: true,
    collection_centers: true,
    producers: true,
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const resp = await api.get("map-locations");
      if (resp.data.success) {
        const { summary: s, ...locs } = resp.data.data;
        setLocations(locs);
        setSummary(s || {});
      }
    } catch (error) {
      console.error("Failed to fetch map locations:", error);
    }
    setPageLoad(false);
  };

  const onMarkerClick = useCallback((markerProps, marker) => {
    setSelectedPlace(markerProps.data);
    setActiveMarker(marker);
    setShowInfoWindow(true);
  }, []);

  const onInfoWindowClose = useCallback(() => {
    setActiveMarker(null);
    setShowInfoWindow(false);
    setSelectedPlace(null);
  }, []);

  const onMapClicked = useCallback(() => {
    if (showInfoWindow) {
      setActiveMarker(null);
      setShowInfoWindow(false);
      setSelectedPlace(null);
    }
  }, [showInfoWindow]);

  const toggleFilter = (type) => {
    setFilters(prev => ({ ...prev, [type]: !prev[type] }));
  };

  // Get all visible markers for bounds
  const getVisibleMarkers = () => {
    let markers = [];
    Object.keys(ENTITY_TYPES).forEach(type => {
      if (filters[type] && locations[type]) {
        const filtered = searchQuery
          ? locations[type].filter(item =>
              (item.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
              (item.village || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
              (item.districtName || '').toLowerCase().includes(searchQuery.toLowerCase())
            )
          : locations[type];
        markers = markers.concat(filtered.map(item => ({ ...item, entityType: type })));
      }
    });
    return markers;
  };

  const visibleMarkers = getVisibleMarkers();
  const totalVisible = visibleMarkers.length;

  // Calculate center from visible markers
  const getCenter = () => {
    if (visibleMarkers.length === 0) return { lat: 20.5937, lng: 78.9629 }; // India center
    const avgLat = visibleMarkers.reduce((s, m) => s + parseFloat(m.lat), 0) / visibleMarkers.length;
    const avgLng = visibleMarkers.reduce((s, m) => s + parseFloat(m.lng), 0) / visibleMarkers.length;
    return { lat: avgLat, lng: avgLng };
  };

  const center = getCenter();

  return (
    <>
      {pageLoad ? (
        <div className="pageLoader"><img src={PageLoader} alt="Loading..." /></div>
      ) : (
        <div className="dashBody" style={{ height: 'calc(100vh - 70px)' }}>
          {/* Header */}
          <div className="font-size-18 mb-0 dashHeader">
            <Row>
              <Col md={4}>
                <h4>🗺️ Nextgen Dairy Map View</h4>
              </Col>
              <Col md={4}>
                <div className="input-group">
                  <div className="input-group-text"><i className="fas fa-search"></i></div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name, village, district..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button className="btn btn-outline-secondary" onClick={() => setSearchQuery('')}>
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </Col>
              <Col md={4} style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '14px', color: '#6c757d' }}>
                  Showing <b>{totalVisible}</b> of <b>{summary.total || 0}</b> locations
                </span>
              </Col>
            </Row>
          </div>

          {/* Filter Pills */}
          <div style={{ padding: '8px 16px', background: '#f8f9fa', borderBottom: '1px solid #dee2e6', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: '#6c757d', marginRight: '4px' }}>Filter:</span>
            {Object.keys(ENTITY_TYPES).map(type => (
              <button
                key={type}
                onClick={() => toggleFilter(type)}
                style={{
                  padding: '4px 12px',
                  borderRadius: '20px',
                  border: `2px solid ${ENTITY_TYPES[type].color}`,
                  background: filters[type] ? ENTITY_TYPES[type].color : 'white',
                  color: filters[type] ? 'white' : ENTITY_TYPES[type].color,
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {ENTITY_TYPES[type].icon} {ENTITY_TYPES[type].label}
                {summary[type] > 0 && ` (${summary[type]})`}
              </button>
            ))}
          </div>

          {/* Map */}
          <div style={{ position: 'relative', height: 'calc(100% - 110px)' }}>
            <Map
              google={props.google}
              style={{ width: '100%', height: '100%' }}
              zoom={visibleMarkers.length > 0 ? 7 : 5}
              center={center}
              initialCenter={{ lat: 20.5937, lng: 78.9629 }}
              onClick={onMapClicked}
            >
              {visibleMarkers.map((item, idx) => (
                <Marker
                  key={`${item.entityType}-${item.id}-${idx}`}
                  title={item.name || 'Unknown'}
                  name={item.name || 'Unknown'}
                  position={{ lat: parseFloat(item.lat), lng: parseFloat(item.lng) }}
                  data={item}
                  onClick={onMarkerClick}
                  icon={{
                    url: `https://maps.google.com/mapfiles/ms/icons/${
                      item.entityType === 'companies' ? 'red' :
                      item.entityType === 'plants' ? 'green' :
                      item.entityType === 'chilling_centers' ? 'blue' :
                      item.entityType === 'collection_centers' ? 'yellow' :
                      'purple'
                    }-dot.png`,
                    scaledSize: new props.google.maps.Size(36, 36),
                  }}
                />
              ))}

              <InfoWindow
                marker={activeMarker}
                visible={showInfoWindow}
                onClose={onInfoWindowClose}
              >
                {selectedPlace ? (
                  <div style={{ minWidth: '200px', padding: '4px' }}>
                    <div style={{
                      background: ENTITY_TYPES[selectedPlace.entityType]?.color || '#333',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      display: 'inline-block',
                      marginBottom: '6px'
                    }}>
                      {ENTITY_TYPES[selectedPlace.entityType]?.icon} {ENTITY_TYPES[selectedPlace.entityType]?.label}
                    </div>
                    <h5 style={{ margin: '4px 0', fontSize: '15px' }}>{selectedPlace.name}</h5>
                    {selectedPlace.village && <p style={{ margin: '2px 0', fontSize: '13px', color: '#555' }}>📍 {selectedPlace.village}{selectedPlace.taluk ? `, ${selectedPlace.taluk}` : ''}</p>}
                    {selectedPlace.districtName && <p style={{ margin: '2px 0', fontSize: '13px', color: '#555' }}>🏛️ {selectedPlace.districtName}, {selectedPlace.stateName}</p>}
                    {selectedPlace.pincode && <p style={{ margin: '2px 0', fontSize: '13px', color: '#555' }}>📮 {selectedPlace.pincode}</p>}
                  </div>
                ) : <div></div>}
              </InfoWindow>
            </Map>
          </div>
        </div>
      )}
    </>
  );
};

MapView.propTypes = {
  google: PropTypes.object
};

export default connect(null)(
  GoogleApiWrapper({
    apiKey: "AIzaSyAbvyBxmMbFhrzP9Z8moyYr6dCr-pzjhBE",
    LoadingContainer: LoadingContainer,
    v: "3",
  })(MapView)
);